import _ from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "@/features/utils/i18n";
import { ExceptionsEditor, type Exception } from "./exceptions-editor";
import { TimeSlots } from "./time-slots";

interface TimeSlot {
  id: string;
  from: string;
  to: string;
}

interface WeekSchedule {
  [day: string]: TimeSlot[];
}

interface RRulesEditorProps {
  value: string;
  onChange: (rrules: string) => void;
  /**
   * Show the holiday/exception editor. Off for callers that only want the
   * weekly grid — onboarding, where closing days are a later concern and an
   * extra section would bury the one thing being reviewed.
   */
  showExceptions?: boolean;
}

function useDaysOfWeek() {
  const { t } = useTranslation("schedules");
  return [
    { key: "MO", label: t("days.monday") },
    { key: "TU", label: t("days.tuesday") },
    { key: "WE", label: t("days.wednesday") },
    { key: "TH", label: t("days.thursday") },
    { key: "FR", label: t("days.friday") },
    { key: "SA", label: t("days.saturday") },
    { key: "SU", label: t("days.sunday") },
  ];
}

function parseRRule(rrule: string): {
  schedule: WeekSchedule;
  exceptions: Exception[];
} {
  if (!rrule) return { schedule: {}, exceptions: [] };

  const schedule: WeekSchedule = {};
  const exceptions: Exception[] = [];
  const rules = rrule.split("\n").filter((r) => r.trim());

  rules.forEach((rule) => {
    // Parse weekly schedule rules - new format with BYHOUR/BYMINUTE/DURATION
    const scheduleMatch = rule.match(
      /RRULE:FREQ=WEEKLY;BYDAY=([A-Z,]+);BYHOUR=(\d{1,2});BYMINUTE=(\d{1,2});DURATION=(\d+)/,
    );
    if (scheduleMatch) {
      const [, days, startHour, startMin, durationMin] = scheduleMatch;
      const startMinutes = parseInt(startHour) * 60 + parseInt(startMin);
      const endMinutes = startMinutes + parseInt(durationMin);

      const from = `${startHour.padStart(2, "0")}:${startMin.padStart(2, "0")}`;
      const endHour = Math.floor(endMinutes / 60);
      const endMin = endMinutes % 60;
      const to = `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`;

      days.split(",").forEach((day) => {
        if (!schedule[day]) schedule[day] = [];
        // Create a UNIQUE slot object for each day to avoid shared references
        schedule[day].push({
          id: `slot-${Date.now()}-${Math.random()}`,
          from,
          to,
        });
      });
      return;
    }

    // Parse exception rules - support both RRULE-based and date-based
    const exceptionRRuleMatch = rule.match(
      /EXCEPTION:RRULE=([^;]+);NAME=([^;]+);SLOTS=(.+)/,
    );
    if (exceptionRRuleMatch) {
      const [, rrule, name, slotsJson] = exceptionRRuleMatch;
      try {
        const slots = JSON.parse(decodeURIComponent(slotsJson));
        exceptions.push({
          id: `exception-rrule-${Date.now()}-${Math.random()}`,
          rrule: decodeURIComponent(rrule),
          name: decodeURIComponent(name),
          slots,
        });
      } catch (e) {
        console.error("Failed to parse RRULE exception:", e);
      }
      return;
    }

    const exceptionMatch = rule.match(
      /EXCEPTION:DATE=([^;]+);NAME=([^;]+);SLOTS=(.+)/,
    );
    if (exceptionMatch) {
      const [, date, name, slotsJson] = exceptionMatch;
      try {
        const slots = JSON.parse(decodeURIComponent(slotsJson));
        exceptions.push({
          id: `exception-${date}`,
          date,
          name: decodeURIComponent(name),
          slots,
        });
      } catch (e) {
        console.error("Failed to parse exception:", e);
      }
    }
  });

  // Sort all slots chronologically
  Object.keys(schedule).forEach((day) => {
    schedule[day].sort((a, b) => {
      const aMinutes =
        parseInt(a.from.split(":")[0]) * 60 + parseInt(a.from.split(":")[1]);
      const bMinutes =
        parseInt(b.from.split(":")[0]) * 60 + parseInt(b.from.split(":")[1]);
      return aMinutes - bMinutes;
    });
  });

  return { schedule, exceptions };
}

function generateRRule(
  schedule: WeekSchedule,
  exceptions: Exception[],
): string {
  const slotGroups = new Map<string, string[]>();

  Object.entries(schedule).forEach(([day, slots]) => {
    slots.forEach((slot) => {
      const key = `${slot.from}-${slot.to}`;
      if (!slotGroups.has(key)) {
        slotGroups.set(key, []);
      }
      slotGroups.get(key)!.push(day);
    });
  });

  const rrules: string[] = [];

  // Add weekly schedule rules
  slotGroups.forEach((days, timeKey) => {
    const [from, to] = timeKey.split("-");

    const [startHour, startMin] = from.split(":").map(Number);
    const [endHour, endMin] = to.split(":").map(Number);

    // Calculate duration in minutes
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const duration = endMinutes - startMinutes;

    // Sort days alphabetically for consistent RRULE generation
    const byday = days.sort().join(",");

    rrules.push(
      `RRULE:FREQ=WEEKLY;BYDAY=${byday};BYHOUR=${startHour};BYMINUTE=${startMin};DURATION=${duration}`,
    );
  });

  // Add exception rules - support both RRULE-based and date-based
  exceptions.forEach((exception) => {
    const encodedName = encodeURIComponent(exception.name);
    const encodedSlots = encodeURIComponent(JSON.stringify(exception.slots));

    if (exception.rrule) {
      // RRULE-based recurring exception
      const encodedRRule = encodeURIComponent(exception.rrule);
      rrules.push(
        `EXCEPTION:RRULE=${encodedRRule};NAME=${encodedName};SLOTS=${encodedSlots}`,
      );
    } else if (exception.date) {
      // Date-based one-time exception
      rrules.push(
        `EXCEPTION:DATE=${exception.date};NAME=${encodedName};SLOTS=${encodedSlots}`,
      );
    }
  });

  return rrules.join("\n");
}

export function RRulesEditor({
  value,
  onChange,
  showExceptions = true,
}: RRulesEditorProps) {
  const DAYS = useDaysOfWeek();
  const [schedule, setSchedule] = useState<WeekSchedule>({});
  const [exceptions, setExceptions] = useState<Exception[]>([]);

  useEffect(() => {
    const parsed = parseRRule(value);
    setSchedule(parsed.schedule);
    setExceptions(parsed.exceptions);
  }, [value]);

  const updateSchedule = (newSchedule: WeekSchedule) => {
    setSchedule(_.cloneDeep(newSchedule));
    onChange(generateRRule(_.cloneDeep(newSchedule), exceptions));
  };

  const updateExceptions = (newExceptions: Exception[]) => {
    setExceptions(newExceptions);
    onChange(generateRRule(_.cloneDeep(schedule), newExceptions));
  };

  const addMinutesToTime = (time: string, minutes: number): string => {
    const [hours, mins] = time.split(":").map(Number);
    const totalMinutes = hours * 60 + mins + minutes;
    const newHours = Math.min(23, Math.floor(totalMinutes / 60));
    const newMins = totalMinutes % 60;
    return `${String(newHours).padStart(2, "0")}:${String(newMins).padStart(2, "0")}`;
  };

  const addSlot = (day: string) => {
    console.log("Add slot", day);
    const newSchedule = _.cloneDeep(schedule);
    if (!newSchedule[day]) newSchedule[day] = [];

    // Calculate start time based on previous slot
    let from = "09:00";
    let to = "17:00";

    if (newSchedule[day].length > 0) {
      // There's already a slot this day
      const lastSlot = newSchedule[day][newSchedule[day].length - 1];
      const [endHour] = lastSlot.to.split(":").map(Number);

      // If last slot ends before 13:00, assume lunch break and start at 14:00
      if (endHour < 13) {
        from = "14:00";
        to = "19:00";
      } else if (lastSlot?.to) {
        from = addMinutesToTime(lastSlot.to, 15);
        to = addMinutesToTime(from, 480); // 8 hours later
      }
    } else {
      // First slot for this day - check if other days have schedules to copy
      const firstDayWithSchedule = DAYS.find(
        (d) => newSchedule[d.key]?.length > 0,
      );
      if (firstDayWithSchedule) {
        // Deep copy all slots from that day with new IDs
        newSchedule[day] = newSchedule[firstDayWithSchedule.key].map(
          (slot) => ({
            ...slot,
            id: `slot-${Date.now()}-${Math.random()}`,
          }),
        );
        updateSchedule(newSchedule);
        return;
      }
    }

    newSchedule[day].push({
      id: `slot-${Date.now()}-${Math.random()}`,
      from,
      to,
    });
    updateSchedule(newSchedule);
  };

  const removeSlot = (day: string, index: number) => {
    console.log("Remove slot", day, index);
    const newSchedule = _.cloneDeep(schedule);
    newSchedule[day].splice(index, 1);
    if (newSchedule[day].length === 0) delete newSchedule[day];
    updateSchedule(newSchedule);
  };

  const updateSlot = (
    day: string,
    index: number,
    field: "from" | "to",
    value: string,
  ) => {
    if (!field || !value) return;
    console.log("Update slot", day, index, field, value);

    const newSchedule = _.cloneDeep(schedule);
    const currentSlot = newSchedule[day][index];

    if (field === "from") {
      // Ensure 'to' is at least 15 minutes after 'from'

      console.log(
        "before update: ",
        newSchedule["MO"]?.[0],
        newSchedule["TU"]?.[0],
      );

      const minTo = addMinutesToTime(value, 15);
      if (newSchedule[day][index].to <= value) {
        newSchedule[day][index].to = minTo;
      }
      newSchedule[day][index].from = value;

      console.log(
        "after updat 1: ",
        JSON.stringify(newSchedule["MO"]?.[0]),
        JSON.stringify(newSchedule["TU"]?.[0]),
      );

      // Adjust next slot if it overlaps
      if (newSchedule[day][index + 1]) {
        const minNextFrom = addMinutesToTime(currentSlot.to, 15);
        if (newSchedule[day][index + 1].from < minNextFrom) {
          newSchedule[day][index + 1].from = minNextFrom;
          // Ensure the next slot's 'to' is still valid
          const minNextTo = addMinutesToTime(minNextFrom, 15);
          if (newSchedule[day][index + 1].to < minNextTo) {
            newSchedule[day][index + 1].to = minNextTo;
          }
        }
      }
    } else {
      // field === "to"
      // Ensure 'to' is at least 15 minutes after 'from'
      const minTo = addMinutesToTime(currentSlot.from, 15);
      if (value < minTo) {
        value = minTo;
      }
      currentSlot.to = value;

      // Adjust next slot if it overlaps
      if (newSchedule[day][index + 1]) {
        const minNextFrom = addMinutesToTime(value, 15);
        if (newSchedule[day][index + 1].from < minNextFrom) {
          newSchedule[day][index + 1].from = minNextFrom;
          // Ensure the next slot's 'to' is still valid
          const minNextTo = addMinutesToTime(minNextFrom, 15);
          if (newSchedule[day][index + 1].to < minNextTo) {
            newSchedule[day][index + 1].to = minNextTo;
          }
        }
      }
    }

    console.log(
      "after update: ",
      JSON.stringify(newSchedule["MO"]?.[0]),
      JSON.stringify(newSchedule["TU"]?.[0]),
    );

    // Sort slots chronologically to maintain proper order
    Object.keys(newSchedule).forEach((d) => {
      newSchedule[d].sort((a, b) => {
        const aMinutes =
          parseInt(a.from.split(":")[0]) * 60 + parseInt(a.from.split(":")[1]);
        const bMinutes =
          parseInt(b.from.split(":")[0]) * 60 + parseInt(b.from.split(":")[1]);
        return aMinutes - bMinutes;
      });
    });

    updateSchedule(newSchedule);
  };

  console.log(schedule["MO"]?.[0], schedule["TU"]?.[0]);

  return (
    <div className="space-y-4">
      {/* Base Schedule */}
      <div className="space-y-2">
        <div className="space-y-1">
          {DAYS.map(({ key, label }) => (
            <TimeSlots
              key={key}
              label={label}
              slots={schedule[key] || []}
              onAddSlot={() => addSlot(key)}
              onRemoveSlot={(idx) => removeSlot(key, idx)}
              onUpdateSlot={(idx, field, value) =>
                updateSlot(key, idx, field, value)
              }
            />
          ))}
        </div>
      </div>

      {/* Exceptions */}
      {showExceptions && (
        <ExceptionsEditor exceptions={exceptions} onChange={updateExceptions} />
      )}
    </div>
  );
}
